import { useState, useEffect } from 'react';
import { useTheme, themeColors } from '../ThemeContext';
import { Booth } from '../../../mocks/boothData';
import { useTranslation } from 'react-i18next';

interface BoothDetailModalProps {
  booth: Booth | null;
  onClose: () => void;
  onMarkVisited: (id: string) => void;
  onMarkImportant: (id: string) => void;
}

const BoothDetailModal = ({ booth, onClose, onMarkVisited, onMarkImportant }: BoothDetailModalProps) => {
  const { theme } = useTheme();
  const c = themeColors[theme];
  const isDark = theme === 'dark';
  const { t } = useTranslation();
  const [showAI, setShowAI] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteAdded, setNoteAdded] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (booth) {
      setVisible(false);
      const timer = setTimeout(() => setVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [booth]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const handleAddNote = () => {
    if (noteText.trim()) {
      setNoteAdded(true);
      setTimeout(() => setNoteAdded(false), 2000);
      setNoteText('');
    }
  };

  if (!booth) return null;

  const getSponsorLabel = () => {
    if (booth.sponsorLevel === 'platinum') return t('sponsor.platinum');
    if (booth.sponsorLevel === 'gold') return t('sponsor.gold');
    if (booth.sponsorLevel === 'standard') return t('sponsor.standard');
    return t('sponsor.none');
  };

  const getSponsorStyle = () => {
    if (booth.sponsorLevel === 'platinum') return 'bg-gradient-to-r from-slate-300 to-white text-slate-800';
    if (booth.sponsorLevel === 'gold') return 'bg-gradient-to-r from-amber-400 to-yellow-300 text-amber-900';
    return 'bg-gray-500/20 text-gray-400';
  };

  const isLimited = booth.sponsorLevel === 'none';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ maxWidth: '480px', left: '50%', transform: 'translateX(-50%)', width: '100%' }}>
      <div className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`} onClick={handleClose} />
      <div className={`relative w-full rounded-t-3xl ${c.bgModal} border-t ${c.border} transition-transform duration-300 max-h-[85vh] overflow-y-auto ${visible ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className="flex justify-center pt-3 pb-2">
          <div className={`w-10 h-1 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
        </div>
        <div className="px-5 pb-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${getSponsorStyle()}`}>{getSponsorLabel()}</span>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${isDark ? 'bg-[#a78bfa]/20 text-[#a78bfa]' : 'bg-purple-100 text-purple-700'}`}>{booth.boothNumber}</span>
              </div>
              <h2 className={`text-xl font-black ${c.text} leading-tight ${isLimited ? 'blur-[2px]' : ''}`}>{booth.company}</h2>
              <p className={`text-xs ${c.textMuted} mt-0.5`}>{booth.industry} • {booth.hall}</p>
            </div>
            <button onClick={handleClose} className={`w-8 h-8 flex items-center justify-center rounded-full ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'} cursor-pointer whitespace-nowrap`}><i className="ri-close-line"></i></button>
          </div>

          <div className={`flex items-center gap-3 p-3 rounded-xl mb-4 ${isDark ? 'bg-[#a78bfa]/10 border border-[#a78bfa]/20' : 'bg-purple-50 border border-purple-100'}`}>
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${isDark ? 'bg-[#a78bfa]/20' : 'bg-purple-100'}`}>
              <i className={`ri-sparkling-2-line text-lg ${isDark ? 'text-[#a78bfa]' : 'text-purple-600'}`}></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className={`text-xs font-bold ${isDark ? 'text-[#a78bfa]' : 'text-purple-700'}`}>{t('booth.score')}</span>
                <span className={`text-lg font-black ${isDark ? 'text-[#a78bfa]' : 'text-purple-700'}`}>{booth.aiScore}%</span>
              </div>
              <div className={`h-1.5 rounded-full mt-1 ${isDark ? 'bg-[#a78bfa]/20' : 'bg-purple-200'}`}>
                <div className={`h-full rounded-full transition-all duration-700 ${isDark ? 'bg-[#a78bfa]' : 'bg-purple-500'}`} style={{ width: `${booth.aiScore}%` }} />
              </div>
            </div>
          </div>

          <div className={`mb-4 ${isLimited ? 'blur-[3px] select-none' : ''}`}>
            <p className={`text-sm ${c.textSecondary} leading-relaxed`}>{booth.description}</p>
          </div>
          {isLimited && (
            <div className={`text-xs text-center py-2 px-3 rounded-xl mb-4 ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
              <i className="ri-lock-line mr-1"></i>{t('booth.limitedInfo')}
            </div>
          )}

          <div className="flex gap-2 flex-wrap mb-5">
            {booth.tags.map(tag => (
              <span key={tag} className={`text-[11px] font-semibold px-3 py-1 rounded-full ${isDark ? 'bg-[#00d4ff]/10 text-[#00d4ff] border border-[#00d4ff]/20' : 'bg-blue-50 text-blue-700 border border-blue-100'}`}>{tag}</span>
            ))}
          </div>

          {!isLimited && (
            <div className={`p-3 rounded-xl mb-5 ${c.bgSecondary} border ${c.border}`}>
              <p className={`text-[10px] font-bold uppercase tracking-widest ${c.textMuted} mb-2`}>{t('booth.contact')}</p>
              <div className="flex items-center gap-2 mb-1">
                <i className={`ri-mail-line text-sm ${isDark ? 'text-[#00d4ff]' : 'text-[#0070E0]'}`}></i>
                <span className={`text-xs ${c.textSecondary}`}>{booth.contact}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className={`ri-global-line text-sm ${isDark ? 'text-[#00d4ff]' : 'text-[#0070E0]'}`}></i>
                <span className={`text-xs ${c.textSecondary}`}>{booth.website}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-2 mb-5">
            <button onClick={() => onMarkVisited(booth.id)} className={`flex flex-col items-center gap-1 p-3 rounded-xl transition-all active:scale-95 cursor-pointer whitespace-nowrap border ${booth.status === 'visited' ? isDark ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400' : 'bg-emerald-100 border-emerald-200 text-emerald-700' : `${c.bgCard} ${c.border} ${c.textMuted}`}`}>
              <i className="ri-checkbox-circle-line text-lg"></i>
              <span className="text-[10px] font-semibold">{booth.status === 'visited' ? t('booth.visited') : t('booth.markVisited')}</span>
            </button>
            <button onClick={() => onMarkImportant(booth.id)} className={`flex flex-col items-center gap-1 p-3 rounded-xl transition-all active:scale-95 cursor-pointer whitespace-nowrap border ${booth.status === 'important' ? isDark ? 'bg-amber-500/20 border-amber-500/40 text-amber-400' : 'bg-amber-100 border-amber-200 text-amber-700' : `${c.bgCard} ${c.border} ${c.textMuted}`}`}>
              <i className="ri-star-line text-lg"></i>
              <span className="text-[10px] font-semibold">{booth.status === 'important' ? t('booth.important') : t('booth.markImportant')}</span>
            </button>
            <button onClick={() => setShowAI(!showAI)} className={`flex flex-col items-center gap-1 p-3 rounded-xl transition-all active:scale-95 cursor-pointer whitespace-nowrap border ${showAI ? isDark ? 'bg-[#a78bfa]/20 border-[#a78bfa]/40 text-[#a78bfa]' : 'bg-purple-100 border-purple-200 text-purple-700' : `${c.bgCard} ${c.border} ${c.textMuted}`}`}>
              <i className="ri-sparkling-line text-lg"></i>
              <span className="text-[10px] font-semibold">{t('booth.whyAI')}</span>
            </button>
          </div>

          {showAI && (
            <div className={`p-4 rounded-xl mb-5 border ${isDark ? 'bg-[#a78bfa]/10 border-[#a78bfa]/30' : 'bg-purple-50 border-purple-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <i className={`ri-robot-2-line ${isDark ? 'text-[#a78bfa]' : 'text-purple-600'}`}></i>
                <span className={`text-xs font-bold ${isDark ? 'text-[#a78bfa]' : 'text-purple-700'}`}>{t('booth.aiReasoning')}</span>
              </div>
              <p className={`text-xs ${c.textSecondary} leading-relaxed`}>{booth.aiReason}</p>
            </div>
          )}

          <div className={`p-4 rounded-xl border ${c.bgCard} ${c.border}`}>
            <p className={`text-xs font-bold uppercase tracking-widest ${c.textMuted} mb-2`}>{t('booth.quickNote')}</p>
            <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder={t('booth.notePlaceholder')} rows={3}
              className={`w-full text-xs ${c.text} bg-transparent outline-none resize-none`} />
            <button onClick={handleAddNote} className={`mt-2 w-full py-2.5 rounded-xl text-xs font-bold transition-all active:scale-95 cursor-pointer whitespace-nowrap ${noteAdded ? 'bg-emerald-500 text-white' : isDark ? 'bg-[#00d4ff] text-[#08090F]' : 'bg-[#0070E0] text-white'}`}>
              {noteAdded ? t('booth.noteSaved') : t('booth.saveNote')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoothDetailModal;
